import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { SQLiteService } from '../services/SQLiteService';
import { deleteDocumentCompletelyStandalone } from '../utils/documentManagement';

interface DocumentInfo {
  documentId: string;
  filename: string;
  size: number;
  lastModified: Date;
}

export interface AllDocumentsViewRef {
  refreshDocuments: () => void;
}

const AllDocumentsView = forwardRef<AllDocumentsViewRef>((props, ref) => {
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingDocument, setDeletingDocument] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    show: boolean;
    x: number;
    y: number;
    documentId: string;
  } | null>(null);
  const navigate = useNavigate();

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load document databases
      const documentDatabases = await SQLiteService.listDocumentDatabases();
      setDocuments(documentDatabases);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  // Expose refreshDocuments method to parent
  useImperativeHandle(ref, () => ({
    refreshDocuments: loadDocuments
  }));

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
    };

    if (contextMenu?.show) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  const handleContextMenu = (e: React.MouseEvent, documentId: string) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      documentId
    });
  };

  const handleOffloadFromContext = () => {
    if (contextMenu) {
      offloadDocument(contextMenu.documentId);
      setContextMenu(null);
    }
  };

  const handleDeleteFromContext = () => {
    if (contextMenu) {
      deleteDocumentCompletely(contextMenu.documentId);
      setContextMenu(null);
    }
  };


  const offloadDocument = async (documentId: string) => {
    // Show confirmation dialog for offload (local delete only)
    const confirmed = window.confirm(
      `Offload "${documentId}" from this device?\n\nThis will remove the local copy but keep it in the cloud. You can download it again later.`
    );
    
    if (!confirmed) return;

    setDeletingDocument(documentId);
    setError(null);

    try {
      // Delete only the local database
      const success = await SQLiteService.deleteDocumentDatabase(documentId);
      
      if (success) {
        // Refresh the documents list
        await loadDocuments();
      } else {
        setError(`Failed to offload document: ${documentId}`);
      }
    } catch (error) {
      console.error(`Error offloading document ${documentId}:`, error);
      setError(`Error offloading document: ${documentId}`);
    } finally {
      setDeletingDocument(null);
    }
  };

  const deleteDocumentCompletely = async (documentId: string) => {
    // Show confirmation dialog for complete deletion
    const confirmed = window.confirm(
      `⚠️ PERMANENTLY DELETE "${documentId}"?\n\nThis will delete the document from BOTH your device AND the cloud.\n\n🚨 THIS ACTION CANNOT BE UNDONE! 🚨\n\nType "DELETE" to confirm this permanent action.`
    );
    
    if (!confirmed) return;

    // Additional confirmation step
    const confirmText = prompt(
      `To permanently delete "${documentId}", type "DELETE" exactly (all caps):`
    );
    
    if (confirmText !== 'DELETE') {
      alert('Deletion cancelled. You must type "DELETE" exactly to confirm.');
      return;
    }

    setDeletingDocument(documentId);
    setError(null);

    try {
      // Delete from both local and cloud
      const result = await deleteDocumentCompletelyStandalone(documentId);
      
      if (result.localDeleted || result.cloudDeleted) {
        // Refresh the documents list
        await loadDocuments();
        
        let message = `Document "${documentId}" deletion completed:\n`;
        message += `• Local: ${result.localDeleted ? '✅ Deleted' : '❌ Failed'}\n`;
        message += `• Cloud: ${result.cloudDeleted ? '✅ Deleted' : '❌ Failed'}`;
        
        if (result.warnings && result.warnings.length > 0) {
          message += `\n\n⚠️ Warnings:\n${result.warnings.join('\n')}`;
        }
        
        if (result.errors.length > 0) {
          message += `\n\n❌ Errors:\n${result.errors.join('\n')}`;
        }
        
        alert(message);
      } else {
        setError(`Failed to delete document: ${documentId}`);
      }
    } catch (error) {
      console.error(`Error deleting document ${documentId}:`, error);
      setError(`Error deleting document: ${documentId}`);
    } finally {
      setDeletingDocument(null);
    }
  };


  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading documents...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Error loading documents</h3>
        <p>{error}</p>
        <button onClick={loadDocuments} className="retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="documents-container">
      {documents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h4>No Documents Yet</h4>
          <p>Create your first document using the form above</p>
          <p>Each document will get its own SQLite database stored locally</p>
        </div>
      ) : (
        <div className="documents-grid">
          {documents.map((doc) => (
            <div 
              key={doc.documentId} 
              className="document-card"
              onClick={() => navigate(`/${doc.documentId}`)}
              onContextMenu={(e) => handleContextMenu(e, doc.documentId)}
              title="Click to open, right-click for options"
            >
              <div className="document-icon">📄</div>
              <div className="document-info">
                <h4 className="document-name">{doc.documentId}</h4>
                <p className="document-modified">
                  {doc.lastModified.toLocaleDateString()}
                </p>
              </div>
              {deletingDocument === doc.documentId && (
                <div className="deleting-overlay">
                  <div className="deleting-spinner">⏳</div>
                  <p>Deleting...</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Context Menu */}
      {contextMenu?.show && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="context-menu-item"
            onClick={() => navigate(`/${contextMenu.documentId}`)}
          >
            📝 Open
          </div>
          <div className="context-menu-separator"></div>
          <div 
            className="context-menu-item offload"
            onClick={handleOffloadFromContext}
            title="Remove from this device but keep in cloud"
          >
            🗂️ Offload
          </div>
          <div 
            className="context-menu-item delete"
            onClick={handleDeleteFromContext}
            title="Permanently delete from device AND cloud"
          >
            🗑️ Delete Permanently
          </div>
        </div>
      )}

      <style>{`
        .loading-container {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: #6c757d;
        }

        .error-container {
          padding: 2rem;
          background: #f8d7da;
          color: #721c24;
          border-radius: 8px;
          text-align: center;
        }

        .error-container h3 {
          margin: 0 0 1rem 0;
        }

        .error-container p {
          margin: 0 0 1rem 0;
        }

        .retry-btn {
          padding: 0.5rem 1rem;
          background-color: #dc3545;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .retry-btn:hover {
          background-color: #c82333;
        }

        .documents-container {
          width: 100%;
        }

        .empty-state {
          text-align: center;
          padding: 3rem 2rem;
          background: #f8f9fa;
          border-radius: 12px;
          border: 2px dashed #dee2e6;
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .empty-state h4 {
          margin: 0 0 1rem 0;
          color: #495057;
          font-size: 1.25rem;
        }

        .empty-state p {
          margin: 0.5rem 0;
          color: #6c757d;
          line-height: 1.5;
        }

        .documents-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
          padding: 1rem;
          background: #fafafa;
          border-radius: 8px;
        }

        .document-card {
          background: transparent;
          border-radius: 8px;
          padding: 1rem 0.5rem;
          transition: all 0.15s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          cursor: pointer;
          position: relative;
          user-select: none;
        }

        .document-card:hover {
          background: rgba(0, 123, 255, 0.08);
        }

        .document-card:active {
          background: rgba(0, 123, 255, 0.15);
        }

        .document-icon {
          font-size: 4rem;
          margin-bottom: 0.5rem;
          opacity: 0.9;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
        }

        .document-info {
          width: 100%;
        }

        .document-name {
          margin: 0 0 0.25rem 0;
          color: #1d1d1f;
          font-size: 0.875rem;
          font-weight: 500;
          word-break: break-word;
          line-height: 1.2;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .document-modified {
          margin: 0;
          color: #8e8e93;
          font-size: 0.75rem;
          font-weight: 400;
        }

        .deleting-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          color: #666;
        }

        .deleting-spinner {
          font-size: 1.5rem;
          margin-bottom: 0.25rem;
          animation: pulse 1s infinite;
        }

        .deleting-overlay p {
          margin: 0;
          font-weight: 500;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .context-menu {
          background: white;
          border: 1px solid #ddd;
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          overflow: hidden;
          min-width: 120px;
          animation: contextMenuFadeIn 0.1s ease-out;
        }

        .context-menu-item {
          padding: 8px 12px;
          font-size: 0.875rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background-color 0.1s ease;
          user-select: none;
        }

        .context-menu-item:hover {
          background-color: #f5f5f5;
        }

        .context-menu-item.offload:hover {
          background-color: #fff3e0;
          color: #f57c00;
        }

        .context-menu-item.delete:hover {
          background-color: #fee;
          color: #d32f2f;
        }

        .context-menu-separator {
          height: 1px;
          background-color: #e0e0e0;
          margin: 4px 0;
        }

        @keyframes contextMenuFadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }


        @media (max-width: 768px) {
          .documents-grid {
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
            gap: 0.75rem;
            padding: 0.75rem;
          }
          
          .document-card {
            padding: 0.75rem 0.25rem;
          }

          .document-icon {
            font-size: 3rem;
          }

          .document-name {
            font-size: 0.8rem;
          }

          .document-modified {
            font-size: 0.7rem;
          }

          .empty-state {
            padding: 2rem 1rem;
          }
          
          .empty-icon {
            font-size: 2.5rem;
          }
        }
      `}</style>
    </div>
  );
});

AllDocumentsView.displayName = 'AllDocumentsView';

export default AllDocumentsView;
